import { useMemo, useState } from "react";
import { RadioTower } from "lucide-react";
import { DAY_MS } from "@amber/contracts";
import {
  AppLink,
  DataTable,
  cx,
  dataColumns,
  formatBytes,
  formatDateTime,
  formatRelative,
  useNarrow,
  type DataColumn,
  type DataGroup,
} from "@amber/ui";
import { HEALTH_ORDER, platformEntry, type PlatformRow, type SourcesResponse } from "../../contracts/index";
import { ActivityBars, HealthLabel, PlatformCode } from "./SourceVisuals";

function platformGroups(data: SourcesResponse) {
  const rank = (p: PlatformRow) => (p.health.state === null ? 5 : HEALTH_ORDER.indexOf(p.health.state));
  return [
    {
      key: "needs_attention",
      label: "Needs attention",
      note: "Platform-wide issues first, then by sources affected",
      rows: data.platforms.filter((p) => p.issue !== null || (p.collection_state === "collected" && rank(p) <= 2)),
    },
    {
      key: "unusually_quiet",
      label: "Unusually quiet",
      note: "Scans succeed, but some sources have gone quiet",
      rows: data.platforms.filter((p) => p.issue === null && p.health.state === "unusually_quiet"),
    },
    {
      key: "healthy",
      label: "Healthy",
      note: "Most active first",
      rows: data.platforms
        .filter((p) => p.collection_state === "collected" && p.issue === null && p.health.state === "healthy")
        .sort((a, b) => b.items_30d - a.items_30d),
    },
    {
      key: "unmonitored",
      label: "No monitored sources",
      note: "Sources here are in no plan or paused; nothing is scanned",
      rows: data.platforms.filter(
        (p) => p.collection_state === "collected" && p.issue === null && p.health.state === null,
      ),
    },
    {
      key: "not_collected",
      label: "Not collected yet",
      note: "Known accounts only; not a failure",
      rows: data.platforms.filter((p) => p.collection_state !== "collected"),
    },
  ].filter((g) => g.rows.length > 0);
}

const col = dataColumns<PlatformRow>();

const NAME_COLUMNS: readonly DataColumn<PlatformRow>[] = [
  col.display({
    id: "platform",
    header: "Platform",
    meta: { width: 170 },
    cell: ({ row: { original: p } }) => (
      <span className="flex items-center gap-2">
        <PlatformCode code={platformEntry(p.key)?.code ?? p.key.toUpperCase()} />
        <b className="font-strong text-fg">{p.label}</b>
        {p.issue ? <RadioTower size={13} className="text-waiting" aria-label="Platform-wide issue" /> : null}
      </span>
    ),
  }),
  col.display({
    id: "health",
    header: "Health",
    meta: { width: 170 },
    cell: ({ row: { original: p } }) => (
      <span className="flex flex-col leading-tight">
        {p.health.state ? (
          <HealthLabel state={p.health.state} label={p.health.label} />
        ) : (
          <span className="text-muted">—</span>
        )}
        <span className="text-md text-muted">{p.issue ? p.issue.title : p.health.affected}</span>
      </span>
    ),
  }),
  col.display({
    id: "activity",
    header: "Activity · 30 days",
    meta: { width: 190 },
    cell: ({ row: { original: p } }) =>
      p.collection_state === "collected" ? (
        <span className="flex items-center gap-2">
          <ActivityBars daily={p.daily} label={`Items found per day over 30 days on ${p.label}`} />
          <span className="font-mono text-md leading-tight">
            <b className="text-fg">{p.items_7d}</b> 7 d<br />
            {p.items_30d} 30 d
          </span>
        </span>
      ) : (
        <span className="text-md text-muted">Not collected yet</span>
      ),
  }),
  col.display({
    id: "collected",
    header: "Collected · all time",
    meta: { width: 150 },
    cell: ({ row: { original: p } }) =>
      p.items_total > 0 ? (
        <AppLink
          href={`/backlog?platform=${p.key}`}
          className="text-link"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <b className="font-mono">{p.items_total.toLocaleString("en-GB")}</b> items
        </AppLink>
      ) : (
        <span className="text-muted">—</span>
      ),
  }),
];

const WIDE_COLUMNS: readonly DataColumn<PlatformRow>[] = [
  col.display({
    id: "archive",
    header: "Archive size",
    meta: { width: 100 },
    cell: ({ row: { original: p } }) => (
      <span className="font-mono text-fg">{p.archive_bytes > 0 ? formatBytes(p.archive_bytes) : "—"}</span>
    ),
  }),
  col.display({
    id: "problems",
    header: "Download problems",
    meta: { width: 96 },
    cell: ({ row: { original: p } }) =>
      p.download_problems > 0 ? <span className="text-waiting">{p.download_problems}</span> : "",
  }),
  col.display({
    id: "gaps",
    header: "Open gaps",
    meta: { width: 80 },
    cell: ({ row: { original: p } }) =>
      p.open_gaps > 0 ? (
        <AppLink
          href={`/sources?tab=gaps&platform=${p.key}`}
          className="font-strong text-link"
          aria-label={`${String(p.open_gaps)} open gaps on ${p.label}`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {p.open_gaps}
        </AppLink>
      ) : (
        ""
      ),
  }),
  col.display({
    id: "last",
    header: "Last new item",
    meta: { width: 100 },
    cell: ({ row: { original: p } }) =>
      p.last_new_item ? (
        <span
          className={cx(Date.now() - new Date(p.last_new_item).getTime() > 3 * DAY_MS && "font-strong text-fg")}
          title={formatDateTime(p.last_new_item)}
        >
          {formatRelative(p.last_new_item)}
        </span>
      ) : (
        "—"
      ),
  }),
];

const PINNED = ["platform"];

export function PlatformsTable({ data, onOpen }: { data: SourcesResponse; onOpen: (platform: string) => void }) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set(["not_collected"]));
  const narrow = useNarrow();
  const columns = useMemo(() => (narrow ? NAME_COLUMNS : [...NAME_COLUMNS, ...WIDE_COLUMNS]), [narrow]);
  const groups = platformGroups(data).map((g): DataGroup<PlatformRow> => ({
    key: g.key,
    label: g.label,
    note: g.note,
    count: g.rows.length,
    rows: g.rows,
    collapsed: collapsed.has(g.key),
    attention: g.key === "needs_attention",
    onToggle: () => {
      const next = new Set(collapsed);
      if (next.has(g.key)) next.delete(g.key);
      else next.add(g.key);
      setCollapsed(next);
    },
  }));
  return (
    <DataTable
      label="Platforms"
      columns={columns}
      rows={data.platforms}
      rowId={(p) => p.key}
      minWidth={narrow ? 560 : 1060}
      pinned={PINNED}
      groups={groups}
      rowOptions={(p) => ({
        label: `${p.label}, ${p.health.label}, ${p.health.affected}`,
        className: "[&>td]:h-14",
        onOpen: () => {
          onOpen(p.key);
        },
      })}
    />
  );
}
